import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import { type GridColDef, type GridRowsProp } from '@mui/x-data-grid';
import { type FC, useMemo } from 'react';

import EmergencyContactDelete from './EmergencyContactDelete.js';
import EmergencyContactForm from './EmergencyContactForm.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type EmergencyContact } from '../../models/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

interface EmergencyContactsTableProps {
  data?: EmergencyContact[];
  isLoading: boolean;
  patientId: string;
  refetch: () => void;
  title?: string;
}

const EmergencyContactsTable: FC<EmergencyContactsTableProps> = ({ data = [], patientId, refetch, isLoading, title }) => {
  const {open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen} = useDialogControls<EmergencyContact>(data);
  const {open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete} = useDialogControls<EmergencyContact>(data);

  const { canCreateEmergencyContact, canUpdateEmergencyContact, canDeleteEmergencyContact } = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'phoneNumber', headerName: 'Phone number', flex: 1, sortable: false },
      { field: 'relationship', headerName: 'Relationship', flex: 1 },
    ];
    if (canUpdateEmergencyContact || canDeleteEmergencyContact) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canUpdateEmergencyContact,
          canDelete: canDeleteEmergencyContact,
        }),
      });
    }
    return cols;
  }, [canUpdateEmergencyContact, canDeleteEmergencyContact, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(
    () =>
      data.map((item) => ({
        id: item.id,
        name: `${item.firstName} ${item.lastName}`,
        phoneNumber: item.phoneNumber,
        relationship: item.relationship,
      })) || [],
    [data],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ mb: 1.5 }}>
        {title ? (
          <Typography variant={'h5'} component={'h3'}>
            {title}
          </Typography>
        ) : null}
        {canCreateEmergencyContact ? (
          <Button
            variant={'contained'}
            size={'small'}
            startIcon={<AddIcon />}
            aria-label={'Add a new emergency contact'}
            onClick={() => setFormOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Add contact
          </Button>
        ) : null}
      </Stack>
      <Table
        columns={columns}
        rows={rows}
        pageSize={10}
        NoRowsOverlayText={'No emergency contacts available.'}
        isLoading={isLoading}
      />
      <EmergencyContactForm
        open={formOpen}
        onClose={resetForm}
        contact={formData}
        refetch={refetch}
        patientId={patientId}
      />
      <EmergencyContactDelete
        open={deleteOpen}
        onClose={resetDelete}
        contact={deleteData}
        refetch={refetch}
        patientId={patientId}
      />
    </Box>
  );
};

export default EmergencyContactsTable;
